import {
  apiClient,
  circuitBreaker,
  integrationCache,
  rateLimiter,
  type ApiClient,
} from '../api'
import { integrationConnectorRegistry } from '../connectors'
import type { SupplierSearchDTO } from '../dto'
import type {
  IntegrationConfig,
  IntegrationDashboard,
  IntegrationHealth,
  IntegrationStatus,
  SupplierResponse,
} from '../entities'
import {
  integrationConfigRepository,
  integrationHealthRepository,
  integrationLogRepository,
} from '../repositories'
import { credentialService } from '@/modules/suppliers/services'

/**
 * HealthMonitorService — tracks online/offline/latency per supplier.
 */
export class HealthMonitorService {
  async checkAll(): Promise<IntegrationHealth[]> {
    const adapters = integrationConnectorRegistry.list()
    const results: IntegrationHealth[] = []
    for (const adapter of adapters) {
      results.push(await this.checkOne(adapter.supplierCode))
    }
    return results
  }

  async checkOne(supplierId: string): Promise<IntegrationHealth> {
    const adapter = integrationConnectorRegistry.get(supplierId)
    const previous = integrationHealthRepository.get(supplierId)
    const now = new Date().toISOString()

    if (!adapter) {
      const health: IntegrationHealth = {
        supplierId,
        status: 'OFFLINE',
        latency: 0,
        lastCheck: now,
        lastSuccess: previous?.lastSuccess ?? null,
        lastFailure: now,
        averageLatency: previous?.averageLatency ?? 0,
        cacheHitRate: integrationCache.hitRate(),
        lastSync: previous?.lastSync ?? null,
        failureCount: (previous?.failureCount ?? 0) + 1,
      }
      return integrationHealthRepository.upsert(health)
    }

    if (circuitBreaker.getState(supplierId) === 'OPEN') {
      const health: IntegrationHealth = {
        supplierId,
        status: 'CIRCUIT_OPEN',
        latency: previous?.latency ?? 0,
        lastCheck: now,
        lastSuccess: previous?.lastSuccess ?? null,
        lastFailure: now,
        averageLatency: previous?.averageLatency ?? 0,
        cacheHitRate: integrationCache.hitRate(),
        lastSync: previous?.lastSync ?? null,
        failureCount: previous?.failureCount ?? 0,
      }
      return integrationHealthRepository.upsert(health)
    }

    const response = await adapter.healthCheck()
    const latency = response.items[0]?.latency ?? response.executionTime
    const ok = response.status === 'success' && Boolean(response.items[0]?.online)
    const averageLatency = previous
      ? Math.round((previous.averageLatency * 0.7 + latency * 0.3) * 100) / 100
      : latency

    const health: IntegrationHealth = {
      supplierId,
      status: ok ? 'ONLINE' : 'OFFLINE',
      latency,
      lastCheck: now,
      lastSuccess: ok ? now : previous?.lastSuccess ?? null,
      lastFailure: ok ? previous?.lastFailure ?? null : now,
      averageLatency,
      cacheHitRate: integrationCache.hitRate(),
      lastSync: ok ? now : previous?.lastSync ?? null,
      failureCount: ok ? 0 : (previous?.failureCount ?? 0) + 1,
    }

    integrationLogRepository.add({
      supplierId,
      requestId: response.requestId,
      operation: 'healthCheck',
      status: response.status,
      duration: response.executionTime,
      errorMessage: response.errors[0] ?? null,
    })

    return integrationHealthRepository.upsert(health)
  }
}

/**
 * IntegrationService — facade for supplier-agnostic operations.
 */
export class IntegrationService {
  private readonly healthMonitor = new HealthMonitorService()
  private readonly client: ApiClient

  constructor(client: ApiClient = apiClient) {
    this.client = client
  }

  getConfig(): IntegrationConfig {
    return integrationConfigRepository.get()
  }

  updateConfig(patch: Partial<IntegrationConfig>): IntegrationConfig {
    const next = integrationConfigRepository.save(patch)
    rateLimiter.setLimit(next.rateLimitPerMinute)
    rateLimiter.reset()
    return next
  }

  async bootstrap(): Promise<void> {
    integrationConnectorRegistry.ensureDefaults()
    this.client.setMockHandler(async () => {
      await new Promise((r) => setTimeout(r, 5 + Math.random() * 20))
      return { status: 200, data: { ok: true, items: [] } }
    })
    await this.healthMonitor.checkAll()
  }

  listHealth(): IntegrationHealth[] {
    return integrationHealthRepository.list()
  }

  async refreshHealth(): Promise<IntegrationHealth[]> {
    return this.healthMonitor.checkAll()
  }

  dashboard(): IntegrationDashboard {
    const suppliers = this.listHealth()
    const averageLatency =
      suppliers.length === 0
        ? 0
        : Math.round(
            (suppliers.reduce((s, h) => s + h.averageLatency, 0) / suppliers.length) * 100,
          ) / 100
    const maxLatency = suppliers.reduce((max, h) => Math.max(max, h.latency), 0)
    const unavailableCount = suppliers.filter((h) => h.status !== 'ONLINE').length
    return {
      averageLatency,
      maxLatency,
      unavailableCount,
      cacheHitRate: integrationCache.hitRate(),
      suppliers,
    }
  }

  logs(supplierId?: string) {
    return integrationLogRepository.list(supplierId)
  }

  async search(supplierCode: string, query: SupplierSearchDTO) {
    return this.run(supplierCode, 'searchProducts', async (adapter) => {
      await this.hydrateAuth(adapter.supplierCode)
      return adapter.searchProducts(query)
    })
  }

  async getProduct(supplierCode: string, id: string) {
    return this.run(supplierCode, 'getProduct', async (adapter) => {
      await this.hydrateAuth(adapter.supplierCode)
      return adapter.getProduct(id)
    })
  }

  private async hydrateAuth(supplierCode: string): Promise<void> {
    const secrets = await credentialService.decryptForConnector(supplierCode)
    const adapter = integrationConnectorRegistry.get(supplierCode)
    if (!adapter || !secrets) return
    await adapter.authenticate({
      username: secrets.username,
      password: secrets.password,
      apiKey: secrets.apiKey,
      apiSecret: secrets.apiSecret,
      token: secrets.token,
      refreshToken: secrets.refreshToken,
    })
  }

  private async run(
    supplierCode: string,
    operation: string,
    fn: (adapter: NonNullable<ReturnType<typeof integrationConnectorRegistry.get>>) => Promise<SupplierResponse>,
  ): Promise<SupplierResponse> {
    const adapter = integrationConnectorRegistry.get(supplierCode)
    if (!adapter) {
      return {
        supplier: supplierCode,
        status: 'error',
        executionTime: 0,
        requestId: crypto.randomUUID(),
        items: [],
        errors: ['Adapter não encontrado'],
        warnings: [],
      }
    }

    const response = await fn(adapter)
    integrationLogRepository.add({
      supplierId: supplierCode,
      requestId: response.requestId,
      operation,
      status: response.status,
      duration: response.executionTime,
      errorMessage: response.errors[0] ?? null,
    })

    const previous = integrationHealthRepository.get(supplierCode)
    const status: IntegrationStatus =
      response.status === 'success'
        ? 'ONLINE'
        : circuitBreaker.getState(supplierCode) === 'OPEN'
          ? 'CIRCUIT_OPEN'
          : 'DEGRADED'

    integrationHealthRepository.upsert({
      supplierId: supplierCode,
      status,
      latency: response.executionTime,
      lastCheck: new Date().toISOString(),
      lastSuccess:
        response.status === 'success' ? new Date().toISOString() : previous?.lastSuccess ?? null,
      lastFailure:
        response.status === 'error' ? new Date().toISOString() : previous?.lastFailure ?? null,
      averageLatency: previous
        ? Math.round((previous.averageLatency * 0.7 + response.executionTime * 0.3) * 100) / 100
        : response.executionTime,
      cacheHitRate: integrationCache.hitRate(),
      lastSync: response.status === 'success' ? new Date().toISOString() : previous?.lastSync ?? null,
      failureCount:
        response.status === 'error' ? (previous?.failureCount ?? 0) + 1 : previous?.failureCount ?? 0,
    })

    return response
  }
}

export const healthMonitorService = new HealthMonitorService()
export const integrationService = new IntegrationService()
